import type { FunctionComponent } from 'react'
import React, { useCallback, useRef } from 'react'

import classname from 'classnames'

import { keyPress } from '../../../../../utilities/misc/keyPress'
import { PictureCaptioned } from '../../../../Picture'
import type { Images } from '../../imageType'
import styles from './Thumbnails.module.scss'
import { useScroller } from './Thumbnails.utils'

export const Thumbnails: FunctionComponent<Props> = ({
  className,
  images,
  current,
  onChange,
  thumbnailClickHandler,
}) => {
  const ref = useRef<HTMLUListElement>(null)
  const handleKeyPress = useCallback(keyPress, [])

  const handleClick = useCallback(
    (index: number) => {
      onChange(index)
      thumbnailClickHandler && thumbnailClickHandler(index)
    },
    [onChange, thumbnailClickHandler]
  )

  useScroller({ ref, current, images })

  return (
    <ul ref={ref} className={classname(styles.container, className)}>
      {images.map(({ src, alt, sources, thumbnailAlt }, index) => (
        <li key={`image_${src}`} className={styles.item}>
          <PictureCaptioned
            src={src}
            role="button"
            aria-label={alt}
            alt={thumbnailAlt || alt}
            tabIndex={0}
            sources={sources?.thumb}
            wrapperClassName={styles.wrapper}
            imageClassName={classname(styles.image, current === index && styles.active)}
            onClick={() => handleClick(index)}
            onKeyPress={handleKeyPress}
          />
        </li>
      ))}
    </ul>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Optional Class Name
   */
  className?: string
  /**
   * Array of Image objects
   */
  images: Array<Images>
  /**
   * Number of the current image
   */
  current: number
  /**
   * Function to handle on change
   */
  onChange: (num: number) => void
  /**
   * Optional callback that is invoked when selecting image thumbnails.
   */
  thumbnailClickHandler?: (index: number) => void
}
